import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box, Button, Typography, CircularProgress,
  ToggleButton, ToggleButtonGroup, IconButton, Paper,
  Snackbar, Alert as MuiAlert,
} from "@mui/material";
import { ArrowBack, LooksOne, Layers, CheckCircle } from "@mui/icons-material";
import { gqlRequest, CREATE_BUSINESS_USAGE, CREATE_MULTIPLE_BUSINESS_USAGE } from "../../api/graphql";
import { useAuth } from "../../store/AuthContext";
import { defaultUsageForm } from "../../utils/helpers";
import { COLORS } from "../../theme";
import { PageWrapper, BizAvatar } from "../../components/UI";
import UsageForm from "../../components/UsageForm";

// Plan/identifier fields kept in the form but rendered as disabled/readonly —
// not editable on create, but still shown for context.
const DISABLED_FIELDS = ["locationid", "trialplanid", "paymentid", "subscriptionid", "curruntplanid"];

// Date <-> epoch-ms-string helpers. The API stores startdate/enddate as numeric-string
// epoch ms (e.g. "1635483730000"); the <input type="date"> fields work in "YYYY-MM-DD".
const dateInputToEpochString = (dateStr) => {
  if (!dateStr) return undefined;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return String(d.getTime());
};

// Adds N months to a "YYYY-MM-DD" date-input string and returns a new date-input string.
const addMonthsToDateInput = (dateStr, months) => {
  if (!dateStr || !months) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setMonth(d.getMonth() + Number(months));
  return d.toISOString().slice(0, 10);
};

export default function CreateUsagePage() {
  const { businessid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const biz = location.state?.biz || { entityid: businessid };
  const [mode, setMode] = useState("single");
  const [form, setForm] = useState(() => {
    const base = { ...defaultUsageForm(), businessid };
    // Removed entirely: trial plan end date, plan type, location, all flags.
    delete base.trialplanenddate;
    delete base.plantype;
    delete base.location;
    delete base.isfreeplan;
    delete base.isfullaccess;
    delete base.isactive;
    delete base.deleted;
    // AI unlimited not available — strip from payload so it is never sent.
    delete base.isunlimitedaiquery;
    return base;
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const onChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const showToast = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast(p => ({ ...p, open: false }));

  const handleCreate = async () => {
    if (!form.startdate) { showToast("error", "Start date is required."); return; }
    if (mode === "single" && !form.enddate) { showToast("error", "End date is required."); return; }
    if (mode === "multiple" && (!form.planinterval || Number(form.planinterval) < 1)) {
      showToast("error", "Plan interval is required and must be at least 1 month.");
      return;
    }

    setLoading(true);
    try {
      const vars = { ...form };

      if (mode === "multiple") {
        // End date isn't user-editable in multiple mode — derive it from
        // start date + plan interval (months) instead.
        const computedEndDateInput = addMonthsToDateInput(form.startdate, form.planinterval);
        vars.enddate = computedEndDateInput;
        vars.startdate = form.startdate;
      } else {
        vars.startdate = form.startdate;
        vars.enddate = form.enddate ? form.enddate : undefined;
      }

      // Clean up: remove empty strings → undefined
      Object.keys(vars).forEach(k => { if (vars[k] === "") vars[k] = undefined; });

      // Ensure isunlimitedaiquery is never sent regardless of form state.
      delete vars.isunlimitedaiquery;

      if (mode === "single") {
        await gqlRequest(CREATE_BUSINESS_USAGE, vars, token);
      } else {
        await gqlRequest(CREATE_MULTIPLE_BUSINESS_USAGE, vars, token);
      }

      setSuccess(true);
      showToast("success", "Usage created successfully!");
      setTimeout(() => {
        navigate(`/businesses/${businessid}/usage`, { state: { biz } });
      }, 1500);
    } catch (e) {
      showToast("error", e.message || "Failed to create usage.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate(`/businesses/${businessid}/usage`, { state: { biz } })}
          sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5 }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <BizAvatar biz={biz} size={38} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Create Business Usage</Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>{biz.businessname || businessid}</Typography>
        </Box>
      </Box>

      {/* Mode toggle */}
      <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${COLORS.border}`, mb: 3, boxShadow: "none" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>Record mode</Typography>
        <ToggleButtonGroup
          value={mode} exclusive
          onChange={(_, v) => v && setMode(v)}
          sx={{ gap: 1.5, "& .MuiToggleButtonGroup-grouped": { border: `1px solid ${COLORS.border} !important`, borderRadius: "8px !important" } }}
        >
          <ToggleButton value="single" sx={{
            px: 3, textTransform: "none", fontWeight: 600, fontSize: 13,
            "&.Mui-selected": { bgcolor: COLORS.accentLight, color: COLORS.accent, borderColor: `${COLORS.accent} !important` },
          }}>
            <LooksOne sx={{ mr: 1, fontSize: 18 }} /> Single Record
          </ToggleButton>
          <ToggleButton value="multiple" sx={{
            px: 3, textTransform: "none", fontWeight: 600, fontSize: 13,
            "&.Mui-selected": { bgcolor: COLORS.accentLight, color: COLORS.accent, borderColor: `${COLORS.accent} !important` },
          }}>
            <Layers sx={{ mr: 1, fontSize: 18 }} /> Multiple Records
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ mt: 1.5 }}>
          {mode === "single" ? (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              Creates one usage record for this business with the specified limits.
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              Generates recurring records over multiple months. End date is calculated automatically from Start Date + Plan Interval.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Form — overlaid with a loader while submitting */}
      <Box sx={{ position: "relative" }}>
        {loading && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.6)", borderRadius: 2,
          }}>
            <CircularProgress sx={{ color: COLORS.accent }} />
          </Box>
        )}

        <UsageForm
          form={form}
          onChange={onChange}
          isMultiple={mode === "multiple"}
          showUsage={false}
          showStatus={false}
          disabledFields={DISABLED_FIELDS}
          unlimitedExclude={["aiquerylimit"]}
        />
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/businesses/${businessid}/usage`, { state: { biz } })}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: COLORS.border, color: COLORS.muted, px: 3 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={loading || success}
          sx={{
            bgcolor: COLORS.accent, "&:hover": { bgcolor: "#4338ca" },
            borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4, minWidth: 160,
          }}
        >
          {loading
            ? <CircularProgress size={20} color="inherit" />
            : success
              ? <><CheckCircle sx={{ mr: 1, fontSize: 18 }} />Created!</>
              : `Create ${mode === "multiple" ? "Multiple" : "Usage"}`
          }
        </Button>
      </Box>

      {/* Toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </PageWrapper>
  );
}